// Historique d'un partenaire dans « Mes partenaires » (demande de Jean-Baptiste du 04/11/2026) : les fiches que mon
// organisation lui a envoyées, quand et par qui, s'il les voit encore, et l'affaire transmise avec son étape et sa
// prochaine action. Lecture seule : les partages se gèrent sur la fiche.
import { useGetMany, useLocaleState, useTranslate } from "ra-core";
import { Link } from "react-router";

import { findDealLabel } from "../deals/dealUtils";
import type { Deal, Project, Sale } from "../types";
import type { HistoryRow } from "./partnerships";
import { projectName } from "./projectText";
import { formatDay } from "./projectUtils";
import { useDealPipeline } from "./usePartnerOrg";

export const PartnerHistory = ({
  rows,
  sales,
}: {
  rows: HistoryRow[];
  /** Comptes de mon organisation et partenaires, pour nommer qui a partagé. */
  sales: Sale[];
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { data: projects } = useGetMany<Project>(
    "projects",
    { ids: rows.map((row) => row.projectId) },
    { enabled: rows.length > 0 },
  );
  if (!rows.length)
    return (
      <p className="text-sm text-muted-foreground">
        {translate("resources.partnerships.mine.history.empty")}
      </p>
    );
  const firstName = (id: number | null) =>
    sales.find((s) => Number(s.id) === id)?.first_name ?? "";

  return (
    <ul className="flex flex-col divide-y text-sm">
      {rows.map((row) => {
        const project = projects?.find((p) => p.id === row.projectId);
        const by = firstName(row.sharedBy);
        return (
          <li key={row.projectId} className="flex flex-col gap-0.5 py-2">
            <Link
              to={`/projects/${row.projectId}/show`}
              className="font-medium underline underline-offset-2 break-words"
            >
              {project ? projectName(project, locale) : "…"}
            </Link>
            <span className="text-muted-foreground">
              {translate(
                by
                  ? "resources.partnerships.mine.history.shared_by"
                  : "resources.partnerships.mine.history.shared",
                { date: formatDay(row.sharedAt, locale), name: by },
              )}
              {" · "}
              {row.revokedAt
                ? translate("resources.partnerships.mine.history.revoked", {
                    date: formatDay(row.revokedAt, locale),
                  })
                : translate("resources.partnerships.mine.history.sees")}
            </span>
            {row.deal ? (
              <DealLine deal={row.deal} />
            ) : (
              <span className="text-muted-foreground">
                {translate("resources.partnerships.mine.history.no_deal")}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const DealLine = ({ deal }: { deal: Deal }) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { dealStages } = useDealPipeline(deal);
  const stage = findDealLabel(dealStages, deal.stage) ?? deal.stage;
  return (
    <span>
      {translate("resources.partnerships.mine.history.deal", { stage })}
      {deal.next_action_date && (
        <span
          className={
            deal.next_action_date < new Date().toISOString().slice(0, 10)
              ? "text-destructive"
              : "text-muted-foreground"
          }
        >
          {" · "}
          {translate("resources.partnerships.mine.history.next_action", {
            date: formatDay(deal.next_action_date, locale),
          })}
        </span>
      )}
    </span>
  );
};
